import Vue from "vue";
import Vuex from "vuex";
import persistence from "./persistence";
import socket from "./socket";
import players from "./modules/players";
import session from "./modules/session";
import editionJSON from "../editions.json";
import rolesJSON from "../roles.json";
import fabledJSON from "../fabled.json";
import jinxesJSON from "../hatred.json";
import { normalizeScriptRole, classificationDiagnostics, roleKey } from "../scriptCompatibility";

Vue.use(Vuex);

// helper functions
// ——— Admin Console 内容源（服务器下发，作为现有解析链的额外数据源） ————————
// 这里只扩充“数据来源”，不新建第二套剧本/角色/Fabled 体系：
// - Admin 剧本以 isOfficial:false 的 edition 进入现有 setEdition / sendEdition
//   通道（走的就是现有自定义剧本链路）；
// - Admin 角色标记 isCustom:true，因此会走现有 customRolesStripped 全量下发，
//   房间拿到的是自己的快照，后台改版本不会影响正在进行的房间；
// - Admin Fabled 直接进 state.fabled，因此现有“添加传奇角色”选择器自动可见。
const adminEditions = [];
const adminRoles = [];
const adminFabled = [];
// 每份后台剧本的完整定义独立保存，避免同ID被内置/先加载的剧本抢占。
const adminScriptRoleDefinitions = new Map();
let disabledDefaultScripts = [];
let disabledDefaultRoles = [];

const allRoleDefinitions = () =>
  adminRoles.length ? [...rolesJSON, ...adminRoles] : rolesJSON;

// 只信任从服务器内容库登记的剧本，不能相信房间JSON自行携带的isAdmin。
const isAdminEdition = state =>
  !!(state.edition && editionJSONbyId.get(state.edition.id)?.isAdmin);

const applyEditionImagePreference = state => {
  const { grimoire } = state;
  grimoire.isImageOptIn = isAdminEdition(state)
    ? grimoire.adminImageOptIn[state.edition.id] !== false
    : grimoire.customImageOptIn;
};

const getRolesByEdition = (edition = editionJSON[0]) => {
  const roles = new Map(
    allRoleDefinitions()
      .filter(r => r.edition === edition.id || edition.roles.includes(r.id))
      .sort((a, b) => b.team.localeCompare(a.team))
      .map(role => [role.id, role])
  );
  const inline = adminScriptRoleDefinitions.get(edition.id);
  if (inline) inline.forEach((role, id) => {
    // 剧本内声明的team优先，不能让同ID内置普通角色混入角色表。
    if (role.team === "fabled") roles.delete(id);
    else if (edition.roles.includes(id)) roles.set(id, role);
  });
  roles.forEach((role, id) => { if (role.team === "fabled") roles.delete(id); });
  return edition.isAdmin ? new Map(edition.roles.filter(id => roles.has(id)).map(id => [id, roles.get(id)])) : roles;
};

/**
 * 把当前剧本解析成运行时角色表。
 *
 * 只在内置或 Admin 内容库里能查到该剧本 id 时生效：刷新页面时
 * localStorage 里的剧本会先于 Admin 内容库恢复，那一瞬间还查不到定义，
 * 必须等内容库到达后重新应用一次，否则房间刷新后角色表会退回默认剧本。
 */
const applyCurrentEdition = state => {
  if (!state.edition || !state.edition.id) return false;
  if (!editionJSONbyId.has(state.edition.id)) return false;
  state.edition = editionJSONbyId.get(state.edition.id);
  applyEditionImagePreference(state);
  state.roles = applyLegacyOptions(getRolesByEdition(state.edition), state.session);
  state.otherTravelers = getTravelersNotInEdition(state.edition);
  state.scriptDiagnostics = classificationDiagnostics([...state.roles.values(), ...state.players.fabled], state.edition);
  return true;
};

/**
 * 剧本自带的传奇角色要自动进入本局「传奇角色」区——与房间内上传脚本
 * （EditionModal.parseRoles 里那段 fabled 处理）保持同一语义：
 * 只在明确选择新剧本时启用其声明的传奇。没有传奇也必须清空旧启用列表；
 * 后台目录晚到/同剧本重解析只补定义，不能重新启用说书人已删除的传奇。
 *
 * 内置剧本的 roles 列表只有四个基础阵营，Admin 剧本则把传奇角色单独记在
 * fabledRoles 上（它们不进角色池，走 Fabled 选择器）。
 */
const applyEditionFabled = state => {
  const edition = state.edition || {};
  const ids = [
    ...(Array.isArray(edition.fabledRoles) ? edition.fabledRoles : []),
    ...(Array.isArray(edition.roles) ? edition.roles : [])
  ];
  if (!state.players) return;
  const fabled = [];
  ids.forEach(id => {
    const role =
      adminScriptRoleDefinitions.get(edition.id)?.get(id) ||
      (state.fabled && state.fabled.get(id)) || rolesJSONbyId.get(id);
    if (role && role.team === "fabled" && !fabled.some(item => item.id === role.id)) {
      fabled.push(role);
    }
  });
  state.fabled = buildFabledMap(fabled);
  state.players.fabled = fabled;
};

const updateEdition = (state, edition, selected = false) => {
  const changed = selected || state.edition.id !== edition.id;
  state.edition = edition;
  applyCurrentEdition(state);
  applyEditionImagePreference(state);
  if (changed) applyEditionFabled(state);
  state.scriptDiagnostics = [
    ...state.scriptDiagnostics.filter(item => item.code === "DUPLICATE_ID"),
    ...classificationDiagnostics([...state.roles.values(), ...state.players.fabled], state.edition)
  ];
  // 同一剧本的权威gs/旧版规则重应用不是用户选剧本；不能关闭正在操作的窗口。
  if (changed) state.modals.edition = false;
};

const getTravelersNotInEdition = (edition = editionJSON[0]) => {
  return new Map(
    allRoleDefinitions()
      .filter(
        r =>
          r.team === "traveler" &&
          r.edition !== edition.id &&
          !edition.roles.includes(r.id)
      )
      .map(role => [role.id, role])
  );
};

const set = key => ({ grimoire }, val) => {
  grimoire[key] = val;
};

const toggle = key => ({ grimoire }, val) => {
  if (val === true || val === false) {
    grimoire[key] = val;
  } else {
    grimoire[key] = !grimoire[key];
  }
};

const clean = roleKey;

// global data maps
const editionJSONbyId = new Map(
  editionJSON.map(edition => [edition.id, edition])
);
const rolesJSONbyId = new Map(rolesJSON.map(role => [role.id, role]));
const fabled = new Map(fabledJSON.map(role => [role.id, role]));

/** 内置 + Admin Fabled 的统一 Map（是选择器唯一的数据来源）。 */
const buildFabledMap = extraFabled => {
  const map = new Map();
  fabledJSON.forEach(role => {
    map.set(role.id, { ...role, isDisabled: disabledDefaultRoles.includes(role.id) });
  });
  adminFabled.forEach(role => map.set(role.id, role));
  (extraFabled || []).forEach(role => {
    if (!role || !role.id) return;
    // 房间内已经使用的 Fabled 必须永远能解析出来，因此即便之后被停用也保留。
    // 完整JSON定义不能被同ID内置图标/技能覆盖。
    map.set(role.id, role);
  });
  return map;
};

/** 注册/刷新 Admin 内容库（由 src/adminLibrary.js 在启动时调用）。 */
const registerAdminLibrary = payload => {
  const scripts = (payload && Array.isArray(payload.scripts) ? payload.scripts : []).filter(Boolean);
  const roles = (payload && Array.isArray(payload.roles) ? payload.roles : []).filter(Boolean);
  const disabled = (payload && payload.disabledDefaults) || {};
  disabledDefaultScripts = Array.isArray(disabled.scripts) ? disabled.scripts : [];
  disabledDefaultRoles = Array.isArray(disabled.roles) ? disabled.roles : [];
  adminEditions.length = 0;
  adminRoles.length = 0;
  adminFabled.length = 0;
  adminScriptRoleDefinitions.clear();
  /*
   * 先登记 Admin 角色库（后台「角色库」里维护的正式定义），再登记各剧本内联的
   * 角色定义。两者必须进入同一条 resolver（rolesJSONbyId + adminRoles），
   * 这样 Admin 剧本与房间内上传同一个 JSON 会得到等价的运行时角色语义。
   */
  roles.forEach(role => {
    const entry = { ...normalizeScriptRole(role, new Map([...rolesJSONbyId, ...fabled]), new Set([...fabled.keys()].map(roleKey))), isCustom: true, isAdmin: true };
    // 没有上传 Token 的 Admin 角色沿用项目现有的通用 team 图标机制
    //（与 setCustomRoles 里对自定义角色完全相同的映射），不新增任何图片资源。
    if (!entry.image) {
      entry.imageAlt =
        {
          townsfolk: "good",
          outsider: "outsider",
          minion: "minion",
          demon: "evil",
          fabled: "fabled"
        }[entry.team] || "custom";
    }
    adminRoles.push(entry);
    rolesJSONbyId.set(entry.id, entry);
    if (entry.team === "fabled") adminFabled.push(entry);
  });
  /**
   * 剧本内联角色：规范化规则与 setCustomRoles 对自定义角色完全相同
   * （clean id、团队通用图标、夜晚序号取绝对值、补齐基础字段），
   * 全局只登记未知ID；每份剧本另存完整定义，当前剧本优先，不覆盖全局。
   */
  const registerInlineRole = role => {
    if (!role || !role.id) return;
    role = normalizeScriptRole(role, new Map([...rolesJSONbyId, ...fabled]), new Set([...fabled.keys()].map(roleKey)));
    const id = clean(role.id);
    if (!id) return;
    if (!role.name || !role.ability || !role.team) return;
    const definition = Object.assign({}, customRole, role, {
      id,
      isCustom: true,
      isAdmin: true
    });
    /*
     * 与 setCustomRoles（房间内上传自定义剧本）逐字同一条规则：**无条件**把图标
     * 回退登记为阵营通用图标。
     *
     * 真实缺陷（《夜半狂欢》）：剧本里每个角色都带一个外部 `image` 地址、没有
     * `imageAlt`；这里原来只在「没有 image」时才补 imageAlt，于是角色表 / 行动
     * 顺序在「显示自定义图片」开关关闭时用角色 id（如 `387`）去 require 内置图标
     * → Cannot find module → render 抛错 → 两个弹窗都打不开。房间内上传同一份
     * JSON 一直正常，正是因为 setCustomRoles 会无条件补这一行。
     */
    definition.imageAlt =
      {
        townsfolk: "good",
        outsider: "outsider",
        minion: "minion",
        demon: "evil",
        fabled: "fabled"
      }[definition.team] || "custom";
    definition.firstNight = Math.abs(Number(definition.firstNight) || 0);
    definition.otherNight = Math.abs(Number(definition.otherNight) || 0);
    if (!rolesJSONbyId.has(id) && !adminRoles.some(entry => entry.id === id)) {
      adminRoles.push(definition);
      rolesJSONbyId.set(id, definition);
      if (definition.team === "fabled") adminFabled.push(definition);
    }
    return definition;
  };
  scripts.forEach(script => {
    // 先登记剧本内联的角色定义（含 fabled → 进 Fabled 统一 Map）。
    const definitions = (Array.isArray(script.roleDefinitions) ? script.roleDefinitions : [])
      .map(registerInlineRole).filter(Boolean);
    adminScriptRoleDefinitions.set(script.id, new Map(definitions.map(role => [role.id, role])));
    const scriptRole = id => adminScriptRoleDefinitions.get(script.id).get(id) || rolesJSONbyId.get(id);
    const edition = {
      id: script.id,
      name: script.name,
      author: script.author || "",
      description: script.description || "",
      level: script.level || "",
      townsfolkName: script.townsfolkName || "镇民",
      logo: script.logo || "",
      groupNames: script.groupNames || {},
      /*
       * 剧本里的角色 id 必须和定义用同一条清理规则（clean），否则两边对不上：
       * limpy01 系的剧本 id 常带下划线（例如 20003_122 投毒者、3_87 怪咖），
       * 定义登记时被 clean 成 20003122，而这里如果保留原始 id，角色的
       * getRolesByEdition 匹配就会全部落空——实测整份 30 角色剧本在角色选择器
       * 里只剩 2 个（只有本来就没有下划线的 damselbutton / pithagbutton）。
       * 房间内上传走的是 setCustomRoles，它同样会 clean，所以一直正常。
       * fabled 不进角色池（与 setCustomRoles 和内置剧本一致，它们走 Fabled 选择器）。
       */
      roles: (Array.isArray(script.roles) ? script.roles : [])
        .map(id => clean(String(id)))
        .filter(id => {
          const known = id && scriptRole(id);
          return !!id && !(known && known.team === "fabled");
        }),
      // 剧本自带的传奇角色不进角色池，但要单独记下来：选中这个剧本时它们会像
      // 房间内上传脚本一样自动进入本局「传奇角色」区（见 applyEditionFabled）。
      fabledRoles: (Array.isArray(script.roles) ? script.roles : [])
        .map(id => clean(String(id)))
        .filter(id => {
          const known = id && scriptRole(id);
          return !!id && !!known && known.team === "fabled";
        }),
      isOfficial: false,
      isAdmin: true
    };
    adminEditions.push(edition);
    editionJSONbyId.set(edition.id, edition);
  });
};

// jinxes
let jinxes = {};
try {
  // Note: can't fetch live list due to lack of CORS headers
  // fetch("https://bloodontheclocktower.com/script/data/hatred.json")
  //   .then(res => res.json())
  //   .then(jinxesJSON => {
  jinxes = new Map(
    jinxesJSON.map(({ id, hatred }) => [
      clean(id),
      new Map(hatred.map(({ id, reason }) => [clean(id), reason]))
    ])
  );
  // });
} catch (e) {
  console.error("couldn't load jinxes", e);
}

// base definition for custom roles
const customRole = {
  id: "",
  name: "",
  image: "",
  ability: "",
  edition: "custom",
  firstNight: 0,
  firstNightReminder: "",
  otherNight: 0,
  otherNightReminder: "",
  reminders: [],
  remindersGlobal: [],
  setup: false,
  team: "townsfolk",
  isCustom: true,
  // 只追加压缩字段编号，不能移动既有字段，否则旧协议编号会改变。
  jinxes: [],
  sourceTeam: "",
  sourceId: "",
  classificationReason: "",
  directed: false,
  // 仅追加协议字段；不移动0—18的旧编号。导出器扩展字段原样往返。
  button: null
};

// Older official printings kept by limpy01. We keep the variants as separate
// IDs so a storyteller can deliberately choose them without overwriting the
// current official definition.
const legacyRoleOverrides = {
  balloonist: { id: "balloonistold1", name: "气球驾驶员（旧）", ability: "每个夜晚，你会得知一名不同角色类型的玩家，直到场上所有的角色类型你都得知过一次。[+1 外来者]", firstNight: 72, otherNight: 111 },
  acrobat: { id: "acrobatold1", name: "杂技演员（旧）", ability: "每个夜晚*，如果与你邻近的存活善良玩家之一醉酒或中毒，你死亡。", firstNight: 0, otherNight: 79 },
  lilmonsta: { id: "lilmonstaold1", name: "小怪宝（旧）", ability: "每个夜晚，所有爪牙要秘密决定由哪名玩家来照看小怪宝并且“是恶魔”。每个夜晚*，会有一名玩家死亡。[+1爪牙]", firstNight: 0, otherNight: 64 },
  alchemist: { id: "alchemistold1", name: "炼金术士（旧）", ability: "你拥有一个不在场的爪牙角色的能力。", firstNight: 11, otherNight: 0 },
  lycanthrope: { id: "lycanthropeold1", name: "半兽人（旧）", ability: "每个夜晚*，你要选择一名存活的玩家：如果他是善良的，他死亡，并且当晚不会再有其他玩家死亡。", firstNight: 0, otherNight: 48 }
};

const applyLegacyOptions = (roles, session = {}) => {
  const result = new Map(roles);
  const oldRoles = (session && session.isUseOldRole) || {};
  Object.keys(legacyRoleOverrides).forEach(id => {
    if (!oldRoles[id] || !result.has(id)) return;
    const role = result.get(id);
    result.delete(id);
    result.set(legacyRoleOverrides[id].id, { ...role, ...legacyRoleOverrides[id] });
  });
  const oldOrder = (session && session.isUseOldOrder) || {};
  if (result.has("professor")) {
    result.set("professor", { ...result.get("professor"), otherNight: oldOrder.professor ? 79 : 96 });
  }
  if (result.has("pithag")) {
    result.set("pithag", { ...result.get("pithag"), otherNight: oldOrder.pithag ? 37 : 16 });
  }
  return result;
};

export default new Vuex.Store({
  modules: {
    players,
    session
  },
  state: {
    grimoire: {
      isNight: false,
      isNightOrder: true,
      isPublic: true,
      isMenuOpen: false,
      isStatic: false,
      isMuted: false,
      isImageOptIn: false,
      // 后台安全剧本默认开启；房间上传仍使用用户明确授权的独立偏好。
      customImageOptIn: false,
      adminImageOptIn: {},
      zoom: 0,
      background: ""
    },
    modals: {
      edition: false,
      fabled: false,
      gameState: false,
      nightOrder: false,
      reference: false,
      reminder: false,
      role: false,
      privateRoleNote: false,
      chat: false,
      chatMode: false,
      dayPrivateListen: false,
      rps: false,
      dice: false,
      roles: false,
      offlineDraw: false,
      nightPrompt: false,
      receivedGrimoire: false,
      voteHistory: false
    },
    edition: editionJSONbyId.get("tb"),
    // 仅隔离传奇启用消息，不参与角色/身份/快捷唤醒规则。
    fabledContext: "",
    // Controls which official editions appear when browsing the full role
    // collection. It does not alter the active script or assigned roles.
    selectedEditions: {
      tb: true,
      bmr: true,
      snv: true,
      luf: true,
      hdcs: true,
      syyl: true,
      all: true,
      custom_ankot: true
    },
    roles: getRolesByEdition(),
    otherTravelers: getTravelersNotInEdition(),
    fabled,
    scriptDiagnostics: [],
    adminLibraryRevision: 0,
    // 站点配置（Logo / 公告 / Banner / 服务器人数规则），由 src/adminLibrary.js
    // 从 /api/site 拉取。首次请求结束前首页不渲染这里的兜底品牌，避免
    // 管理员配置到达前短暂闪出“血染钟楼 / 房间大厅 / 默认 Logo”。
    siteReady: false,
    site: {
      siteName: "血染钟楼",
      subtitle: "房间大厅",
      logo: "",
      announcement: null,
      banners: [],
      rules: {
        minPlayers: 5,
        maxPlayers: 20,
        createRoomsEnabled: true,
        maxRooms: 0,
        interactionSameTargetCooldownSeconds: 10,
        interactionDifferentTargetCooldownSeconds: 8
      },
      roomCount: 0,
      libraryVersion: 0
    },
    offlineDrawRoles: [],
    offlineAssignedDraw: null,
    offlineRandomDraw: null,
    offlineDrawRevision: 0,
    jinxes
  },
  getters: {
    /**
     * 剧本选择器使用的完整剧本列表：项目内置（排除被 Admin 停用的）+ Admin 新增。
     * 这是“选择剧本”唯一的来源，不新建第二套列表。
     */
    allEditions: ({ adminLibraryRevision }) => {
      // 注册表是模块级数组；显式依赖版本，避免后台刷新后Vuex仍返回旧缓存。
      void adminLibraryRevision;
      return editionJSON
        .filter(edition => !disabledDefaultScripts.includes(edition.id))
        .map(edition => ({ ...edition }))
        .concat(adminEditions.map(edition => ({ ...edition })));
    },
    /**
     * Return all custom roles, with default values and non-essential data stripped.
     * Role object keys will be replaced with a numerical index to conserve bandwidth.
     * @param roles
     * @returns {[]}
     */
    customRolesStripped: ({ roles }) => {
      const customRoles = [];
      const customKeys = Object.keys(customRole);
      const strippedProps = [
        "firstNightReminder",
        "otherNightReminder",
        "isCustom"
      ];
      roles.forEach(role => {
        if (!role.isCustom) {
          customRoles.push({ id: role.id });
        } else {
          const strippedRole = {};
          for (let prop in role) {
            if (strippedProps.includes(prop)) {
              continue;
            }
            const value = role[prop];
            if (customKeys.includes(prop) && value !== customRole[prop]) {
              strippedRole[customKeys.indexOf(prop)] = value;
            }
          }
          customRoles.push(strippedRole);
        }
      });
      return customRoles;
    },
    rolesJSONbyId: () => rolesJSONbyId
  },
  mutations: {
    setZoom: set("zoom"),
    setBackground: set("background"),
    toggleMuted: toggle("isMuted"),
    toggleMenu: toggle("isMenuOpen"),
    toggleNightOrder: toggle("isNightOrder"),
    toggleStatic: toggle("isStatic"),
    toggleNight: toggle("isNight"),
    // The timeline has four phases while the original grimoire only has a
    // day/night bit.  Explicit assignment lets both controls share one
    // deterministic state instead of each independently toggling the bit.
    setNight(state, isNight) {
      state.grimoire.isNight = !!isNight;
    },
    toggleGrimoire: toggle("isPublic"),
    toggleImageOptIn(state, value) {
      const enabled = typeof value === "boolean" ? value : !state.grimoire.isImageOptIn;
      if (isAdminEdition(state)) {
        state.grimoire.adminImageOptIn = {
          ...state.grimoire.adminImageOptIn,
          [state.edition.id]: enabled
        };
      } else {
        state.grimoire.customImageOptIn = enabled;
      }
      state.grimoire.isImageOptIn = enabled;
    },
    setAdminImageOptIn(state, preferences = {}) {
      state.grimoire.adminImageOptIn = Object.fromEntries(
        Object.entries(preferences || {}).filter(([, value]) => typeof value === "boolean")
      );
      applyEditionImagePreference(state);
    },
    setSelectedEditions(state, selectedEditions = {}) {
      state.selectedEditions = { ...state.selectedEditions, ...selectedEditions };
    },
    /**
     * 注册/刷新 Admin Console 的内容库。
     * 只扩充现有解析链的数据来源：Admin 剧本进 editionJSONbyId，
     * Admin 角色进 rolesJSONbyId，Admin Fabled 进统一的 Fabled Map。
     */
    setAdminLibrary(state, payload = {}) {
      registerAdminLibrary(payload);
      state.adminLibraryRevision++;
      state.fabled = buildFabledMap(
        [
          ...[...state.fabled.values()].filter(role => role.isCustom && !role.isAdmin),
          ...state.players.fabled.filter(role => role && role.isCustom)
        ]
      );
      // Admin 内容库可能在剧本恢复之后才到：这时把当前剧本重新解析一次，
      // 房间刷新后 Admin 剧本的角色表必须和第一次选择时完全一致。
      applyCurrentEdition(state);
    },
    cacheRoomFabledDefinitions(state, definitions = []) {
      // 房间恢复时普通角色表不含传奇；将已公开的完整传奇定义同时恢复到
      // 原选择器目录。只补定义，不增删本局传奇、不发送消息或改变玩家身份。
      const restored = definitions.filter(role =>
        role && role.isCustom && role.team === "fabled" && role.name && role.ability
      );
      if (restored.length) {
        state.fabled = new Map([
          ...state.fabled,
          ...restored.map(role => [role.id, role])
        ]);
      }
    },
    setSite(state, site = {}) {
      state.site = {
        ...state.site,
        ...site,
        rules: { ...state.site.rules, ...((site && site.rules) || {}) }
      };
      state.siteReady = true;
    },
    setSiteReady(state, ready = true) {
      state.siteReady = !!ready;
    },
    closeModal({ modals }, name) {
      if (Object.prototype.hasOwnProperty.call(modals, name)) modals[name] = false;
    },
    toggleModal(state, name) {
      const { modals } = state;
      if (name === "nightPrompt" && state.session.isSpectator) return;
      if (name) {
        modals[name] = !modals[name];
      }
      for (let modal in modals) {
        if (modal === name) continue;
        modals[modal] = false;
      }
    },
    // 线下抽取中的牌堆只保存在当前说书人浏览器：它不属于线上玩家
    // 的私密身份消息，也绝不会被同步给房间内其他人。
    setOfflineDrawRoles(state, roles = []) {
      state.offlineAssignedDraw = null;
      state.offlineRandomDraw = null;
      state.offlineDrawRoles = Array.isArray(roles) ? roles : [];
    },
    beginOfflineDrawRequest(state) { state.offlineDrawRevision++; },
    setOfflineRandomDraw(state, value) { state.offlineRandomDraw = value; },
    setOfflineAssignedDraw(state, value) {
      state.offlineAssignedDraw = value || null;
      if (value) { state.offlineRandomDraw = null; state.offlineDrawRoles = []; }
    },
    /**
     * Store custom roles
     * @param state
     * @param roles Array of role IDs or full role definitions
     */
    setCustomRoles(state, roles) {
      const processedRoles = roles
        .filter(role => role && typeof role === "object")
        // replace numerical role object keys with matching key names
        .map(role => {
          if (role[0]) {
            const customKeys = Object.keys(customRole);
            const mappedRole = {};
            for (let prop in role) {
              if (customKeys[prop]) {
                mappedRole[customKeys[prop]] = role[prop];
              }
            }
            // 压缩包省略了默认team=townsfolk。先解压默认字段再分类，
            // 不能依赖异步Admin角色库已加载，否则F5后自制镇民变unknown。
            return { ...customRole, ...mappedRole };
          } else {
            return role;
          }
        })
        .map(role => normalizeScriptRole(role, new Map([...rolesJSONbyId, ...state.fabled]), new Set([...state.fabled.keys()].map(roleKey))))
        // clean internal ID without mutating the uploaded object
        .map(role => {
          return { ...role, id: clean(role.id) };
        })
        // map existing roles to base definition or pre-populate custom roles to ensure all properties
        .map(
          role =>
            (role.name && role.ability && role.team
              ? Object.assign({}, customRole, role, { isCustom: true })
              : null) ||
            rolesJSONbyId.get(role.id) ||
            state.roles.get(role.id) ||
            Object.assign({}, customRole, role)
        )
        // default empty icons and placeholders, clean up firstNight / otherNight
        .map(role => {
          if (rolesJSONbyId.get(role.id) && role.team !== "fabled") return role;
          role.imageAlt = // map team to generic icon
            {
              townsfolk: "good",
              outsider: "outsider",
              minion: "minion",
              demon: "evil",
              fabled: "fabled"
            }[role.team] || "custom";
          role.firstNight = Math.abs(role.firstNight);
          role.otherNight = Math.abs(role.otherNight);
          return role;
        })
        // filter out roles that don't match an existing role and also don't have name/ability/team
        .filter(role => role.name && role.ability && role.team)
        // sort by team
        .sort((a, b) => b.team.localeCompare(a.team));
      // convert to Map without Fabled
      state.roles = applyLegacyOptions(new Map(
        processedRoles
          .filter(role => role.team !== "fabled")
          .map(role => [role.id, role])
      ), state.session);
      // update Fabled to include custom Fabled from this script
      // （统一 Map：内置 Fabled + Admin Fabled + 本剧本自定义 Fabled，仍然只有一份）
      state.fabled = buildFabledMap(processedRoles.filter(r => r.team === "fabled"));
      state.scriptDiagnostics = classificationDiagnostics(processedRoles, state.edition);
      // update extraTravelers map to only show travelers not in this script
      state.otherTravelers = new Map(
        rolesJSON
          .filter(r => r.team === "traveler" && !roles.some(i => i.id === r.id))
          .map(role => [role.id, role])
      );
    },
    setEdition(state, edition) {
      updateEdition(state, edition);
    },
    selectEdition(state, edition) {
      state.fabledContext = `script-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      updateEdition(state, edition, true);
    },
    setFabledContext(state, context = "") {
      state.fabledContext = String(context || "");
    },
    refreshLegacyRoleDefinitions(state) {
      state.players.players.forEach(player => {
        if (!player.role || !player.role.id) return;
        const baseId = player.role.id.replace(/old1$/, "");
        const role = state.roles.get(player.role.id) || state.roles.get(baseId);
        if (role) player.role = role;
      });
    }
  },
  plugins: [persistence, socket]
});
